import React from 'react';
import styled from '@emotion/styled';
import { PolicyBlock } from '@/types/policy';

const PolicyTextContainer = styled.p<{ $kind: PolicyBlock['kind'] }>`
  margin: ${({ $kind }) => ($kind === 'body' ? '0 0 16px' : '0')};
  color: #212121;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: -0.01em;
  white-space: pre-line;

  ${({ $kind }) => $kind === 'brand' && `
    margin: 0 0 4px;
    font-weight: 600;
  `}

  ${({ $kind }) => $kind === 'effectiveDate' && `
    margin: 0 0 4px;
    font-style: italic;
  `}

  ${({ $kind }) => $kind === 'metadata' && `
    margin: 0 0 2px;
  `}

  ${({ $kind }) => $kind === 'heading' && `
    margin: 28px 0 10px;
    font-size: 1.375rem;
    line-height: 1.25;
    font-weight: 700;
    letter-spacing: -0.02em;
  `}

  ${({ $kind }) => $kind === 'subheading' && `
    margin: 18px 0 6px;
    font-size: 1.125rem;
    line-height: 1.35;
    font-weight: 600;
  `}

  ${({ $kind }) => $kind === 'bullet' && `
    position: relative;
    margin: 0 0 6px 22px;

    &::before {
      content: '•';
      position: absolute;
      left: -16px;
    }
  `}

  ${({ $kind }) => $kind === 'form' && `
    margin: 0 0 16px;
    padding: 12px 16px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #f9fafb;
  `}

  ${({ $kind }) => $kind === 'spacer' && `
    height: 16px;
  `}
`;

const Link = styled.a`
  color: #155e75;
  text-decoration: underline;
  cursor: pointer;
  
  &:hover {
    color: #0f3f50;
  }
`;

interface PolicyTextProps {
  block: PolicyBlock;
}

export function PolicyText({ block }: PolicyTextProps) {
  // Регулярные выражения для поиска URL и email
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

  // Функция для рендеринга текста с ссылками и email
  const renderTextWithLinks = () => {
    // Сначала разделяем по URL
    const urlParts = block.text.split(urlRegex);

    // Затем для каждой части проверяем email
    const result: React.ReactNode[] = [];

    urlParts.forEach((part, urlIndex) => {
      if (/^https?:\/\/[^\s]+$/.test(part)) {
        // Это URL - делаем кликабельным
        const url = part.replace(/[.,;:!?)]*$/, '');
        const trailingText = part.slice(url.length);
        result.push(
          <Link
            key={`url-${urlIndex}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {url}
          </Link>,
        );
        if (trailingText) {
          result.push(trailingText);
        }
      }
      else {
        // Это не URL - проверяем на email
        const emailParts = part.split(emailRegex);

        emailParts.forEach((emailPart, emailIndex) => {
          if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailPart)) {
            // Это email - делаем кликабельным
            result.push(
              <Link
                key={`email-${urlIndex}-${emailIndex}`}
                href={`mailto:${emailPart}`}
              >
                {emailPart}
              </Link>,
            );
          }
          else {
            // Это обычный текст
            result.push(emailPart);
          }
        });
      }
    });

    return result;
  };

  return (
    <PolicyTextContainer as={block.kind === 'heading' ? 'h2' : block.kind === 'subheading' ? 'h3' : 'p'} $kind={block.kind}>
      {renderTextWithLinks()}
    </PolicyTextContainer>
  );
}
